/** tm/components/CompanyDetailPanel.tsx — 업체 상세 5탭 (CompanyDetailCol3 TS 전환) */
import React, { useState, useEffect, useRef } from 'react';
import type { Company, Task, Consultation, FP, WM, GovProgram, TaskTemplate, AIPreset, TaskStep, TaskCategory, TaskDecisionItem, CompanyManager } from '../types';
import { extraManagersOf, inConsult } from '../types';
import { C, T, R, Badge, StatusBadge, Empty, Btn } from './ui';
import { dDay } from '../hooks';
import CallRecordings from './CallRecordings';
import CompanySmsLog from './CompanySmsLog';   // 보낸 문자(2026-10-28)
import CommentSection from './CommentSection';
import CompanyLegalSection from '../legal/CompanyLegalSection';
import TaskStepDetail from './TaskStepDetail';
// import { TagEditor, COMPANY_SUGGEST_TAGS } from './TagEditor'; // replaced by category pills
import TaskRegisterModal from './TaskRegisterModal';
import ConsultEditModal from './ConsultEditModal';
import ConsultResultModal from './ConsultResultModal';
import * as api from '../api';
import NowBar from '../flow/NowBar';
import { useFlow, STOP_REASONS, nextRound } from '../flow/FlowActions';
import { CLOSE_PREFIX, STAFF, isClosed, md as flowMd, whoOf } from '../flow/flowLogic';
import type { FinanceData } from '../api';
import CompanyMatchFields, { type MatchFieldsValue, matchFieldsFromCompany, otherTagsOf, mergeTags } from './CompanyMatchFields';
import GovRecoList from './GovRecoList';
import GovWatchSection from './GovWatchSection';   // 공고 나오면 알림(2026-10-30)
import FpFitLine from './FpFitLine';
import PersonPicker from './PersonPicker';
import WmMultiPicker from './WmMultiPicker';
import { companyWmIds } from '../wmList';

const STEP_TYPE_OPTIONS: { key: string; label: string; icon: string; color: string; bg: string }[] = [
  { key: 'work', label: '일반', icon: '⚙️', color: '#6B7280', bg: '#F3F4F6' },
  { key: 'collect', label: '자료수집', icon: '📂', color: '#2563EB', bg: '#EFF6FF' },
  { key: 'ai_questionnaire', label: 'AI질문지', icon: '🤖', color: '#7C3AED', bg: '#F3E8FF' },
  { key: 'ai_bizplan', label: 'AI사업계획', icon: '📊', color: '#7C3AED', bg: '#F3E8FF' },
  { key: 'confirm', label: '확인', icon: '✅', color: '#D97706', bg: '#FEF3C7' },
  { key: 'meeting', label: '미팅', icon: '🤝', color: '#059669', bg: '#ECFDF5' },
  { key: 'report', label: '보고', icon: '📤', color: '#2E75B6', bg: '#E8F0FE' },
  { key: 'contract', label: '계약', icon: '📝', color: '#059669', bg: '#ECFDF5' },
  { key: 'invoice', label: '청구', icon: '🧾', color: '#D97706', bg: '#FEF3C7' },
];

function getStepTypeInfo(key: string) {
  return STEP_TYPE_OPTIONS.find(c => c.key === key) || STEP_TYPE_OPTIONS[0];
}

interface TagsStructured {
  required: string[];
  recommended: string[];
  interests: string[];
}

function parseTagsStructured(tags: string[] | TagsStructured | null | undefined): TagsStructured {
  if (!tags) return { required: [], recommended: [], interests: [] };
  if (Array.isArray(tags)) return { required: tags, recommended: [], interests: [] };
  if (typeof tags === 'object' && 'required' in tags) return { required: tags.required || [], recommended: tags.recommended || [], interests: (tags as TagsStructured).interests || [] };
  return { required: [], recommended: [], interests: [] };
}

function flattenTags(t: TagsStructured): string[] {
  return [...(t.required || []), ...(t.recommended || []), ...((t as TagsStructured & {interests?:string[]}).interests || [])];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseTags(tags: any): string[] {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') { try { return JSON.parse(tags); } catch { return []; } }
  return [];
}

interface StorageFile {
  id: string;
  file_name: string;
  s3_key: string;
  source: string;
  created_at: string;
  uploaded_at?: string;
  sensitive?: boolean;
  item_title?: string;
}

interface CollectFile {
  id: string;
  file_name: string;
  s3_key: string;
  project_name?: string;
  status?: string;
}

interface Props {
  company: Company;
  tasks: Task[];
  consultations: Consultation[];
  fps: FP[];
  govPrograms?: GovProgram[];
  taskTemplates?: TaskTemplate[];
  taskCategories?: TaskCategory[];
  aiPresets?: AIPreset[];
  onClose?: () => void;
  onUpdate: (id: string, patch: Parameters<typeof api.updateCompany>[1]) => Promise<void>;   // 담당 FP · WM 목록(fp_ids · wm_ids)도 — 그 업체 한 곳만 갱신(2026-10-26)
  onNavigateGov?: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
  onRefresh: () => Promise<void>;
  initialTaskId?: string | null;
  /** 대시보드 「공고 나옴 → 업무 만들기」 — 업무 탭 · 그 기다림 강조 · 업무 등록 열기(저장하면 알림 끝) */
  initialWatch?: { id: string; name: string } | null;
}

type Tab = 'info' | 'tasks' | 'comm' | 'storage' | 'finance';

export default function CompanyDetailPanel({
  company, tasks, consultations, fps, taskTemplates = [], taskCategories = [], aiPresets = [],
  onClose, onUpdate, onNavigateGov, onToast, onRefresh, initialTaskId, initialWatch,
}: Props) {
  const [tab, setTab] = useState<Tab>(initialTaskId ? 'tasks' : 'info');
  useEffect(() => {
    console.log('[CDP-DEBUG] initialTaskId:', initialTaskId, 'company:', company.name);
    if (initialTaskId) {
      console.log('[CDP-DEBUG] Setting tab=tasks, expandTask=', initialTaskId);
      setTimeout(() => { setTab('tasks'); setExpandTask(initialTaskId); }, 100);
    }
  }, [initialTaskId, company.name]);
  const [editInfo, setEditInfo] = useState(false);
  const [stopFor, setStopFor] = useState<string | null>(null);   // 업무 중단 이유 고르기 (2026-10-12)
  const [stopOther, setStopOther] = useState('');
  const { run: flowRun, undoNode } = useFlow(() => onRefresh(), m => onToast(m, 'error'));
  const [clientLink, setClientLink] = useState<{ path?: string; open: boolean; confirm: boolean }>({ open: false, confirm: false });   // 고객 페이지 링크 (2026-10-12)
  const [form, setForm] = useState({ ...company });
  // 담당 직원 더(첫 담당자는 manager 칸 · 2026-10-18 지도사 「담당직원은 여러 명 더 추가할 수 있도록」)
  const [mgrs, setMgrs] = useState<CompanyManager[]>(() => extraManagersOf(company));
  const [financeData, setFinanceData] = useState<FinanceData[] | null>(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [expandTask, setExpandTask] = useState<string | null>(initialTaskId || null);
  const [editingDeadline, setEditingDeadline] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 담당 WM 고르기용 목록 — PersonPicker 에서 새로 등록한 WM 도 바로 보이도록 로컬 상태로 둔다
  const [wms, setWms] = useState<WM[]>([]);
  useEffect(() => { api.getWms().then(r => setWms(r.data || [])).catch(() => { /* noop */ }); }, []);
  const curFpIds = (company.fps || []).map(cf => cf.fp_id).filter(Boolean);
  // 담당 FP 추가 — 기존 fp_ids 뒤에 붙여 저장(중복은 PersonPicker exclude 로 이미 막힘)
  const addFp = async (id: string) => {
    if (!id || curFpIds.includes(id)) return;
    try { await onUpdate(company.id, { fp_ids: [...curFpIds, id] }); onToast('FP가 추가되었습니다'); }   // 그 업체 한 곳만 갱신(2026-10-26 · 전체를 다시 받지 않음)
    catch (e: unknown) { onToast(e instanceof Error ? e.message : 'FP 추가 실패', 'error'); }
  };
  const removeFp = async (id: string) => {
    try { await onUpdate(company.id, { fp_ids: curFpIds.filter(x => x !== id) }); onToast('FP가 제거되었습니다'); }
    catch { onToast('FP 제거 실패', 'error'); }
  };
  // 담당 WM 여러 명(공동관리 · 2026-10-26) — 더하기 · 빼기 · 대표로 바꾸면 바로 저장 · 첫 분이 대표(보고서 받는 분)
  const curWmIds = companyWmIds(company);
  const [wmDraft, setWmDraft] = useState<string[] | null>(null);   // 저장 중에도 고른 대로 보이게
  const setWmList = async (ids: string[]) => {
    if (ids.join() === curWmIds.join()) return;
    const added = ids.length > curWmIds.length, removed = ids.length < curWmIds.length;
    setWmDraft(ids);
    // 2026-10-26 지도사 「WM 추가하기 누르면 2초 정도 있다가 추가가 되네」 — 저장 뒤 업무관리 전체(업체 · 업무 · 공고 …)를 다시 받던 것 → 그 업체 한 곳만
    try { await onUpdate(company.id, { wm_ids: ids }); onToast(added ? 'WM이 추가되었습니다' : removed ? 'WM이 빠졌습니다' : '대표 WM이 바뀌었습니다'); }
    catch { onToast('WM 변경 실패', 'error'); }
    finally { setWmDraft(null); }
  };
  const [editConsult, setEditConsult] = useState<Consultation | null>(null);
  const [resultConsult, setResultConsult] = useState<Consultation | null>(null);

  // A-2: company categories (stored in tags field)

  // A-6: storage
  const [storageFiles, setStorageFiles] = useState<StorageFile[]>([]);
  const [previewFile, setPreviewFile] = useState<{url:string,name:string,isImg:boolean}|null>(null);
  const [collectFiles, setCollectFiles] = useState<CollectFile[]>([]);
  const [storageLoading, setStorageLoading] = useState(false);

  // A-7: task registration modal
  const [showTaskModal, setShowTaskModal] = useState<false|'template'|'custom'>(false);
  // 공고 나오면 알림(2026-10-30) — 입력 열기(추천 공고에서 오면 그 공고) · 「신청 업무 만들기」로 연 업무 등록(저장하면 그 알림 끝) · 다시 불러오기
  const [watchForm, setWatchForm] = useState<{ programId?: string } | null>(null);
  const [taskForWatch, setTaskForWatch] = useState<{ id: string; name: string } | null>(null);
  const [watchReload, setWatchReload] = useState(0);
  const [focusWatch, setFocusWatch] = useState<string | null>(null);
  useEffect(() => {
    if (!initialWatch) return undefined;
    // 업체가 바뀔 때 비우는 효과(아래 company.id)보다 뒤에 — initialTaskId 와 같은 방식
    const t = setTimeout(() => { setTab('tasks'); setFocusWatch(initialWatch.id); setTaskForWatch(initialWatch); setShowTaskModal('template'); }, 120);
    return () => clearTimeout(t);
  }, [initialWatch]);

  // company가 바뀌면 탭/폼 초기화
  useEffect(() => { setTab('info'); setEditInfo(false); setForm({ ...company }); setMgrs(extraManagersOf(company)); setFinanceData(null); setStorageFiles([]); setCollectFiles([]); setWatchForm(null); setTaskForWatch(null); setFocusWatch(null); }, [company.id]);

  // 매칭 정보(CompanyMatchFields) — company prop 이 onUpdate 후 상위(store)에서 새로고침되어 항상 최신이므로
  // 로컬 저장소 없이 매번 새로 만든다. 연속 입력(타이핑)만 짧게 모아 한 번에 저장한다(디바운스 즉시저장).
  const [matchPending, setMatchPending] = useState<Partial<MatchFieldsValue>>({});
  const matchPendingRef = useRef<Partial<MatchFieldsValue>>({});
  const matchTimerRef = useRef<number | null>(null);
  useEffect(() => {
    matchPendingRef.current = {}; setMatchPending({});
    if (matchTimerRef.current) { window.clearTimeout(matchTimerRef.current); matchTimerRef.current = null; }
  }, [company.id]);
  const matchValue: MatchFieldsValue = { ...matchFieldsFromCompany(company), ...matchPending };
  async function flushMatch() {
    const patch = matchPendingRef.current;
    if (!Object.keys(patch).length) return;
    const next = { ...matchFieldsFromCompany(company), ...patch };
    const apiPatch: Record<string, unknown> = {};
    if ('region' in patch) apiPatch.region = next.region;
    if ('region_detail' in patch) apiPatch.region_detail = next.region_detail;
    // biz_type 을 biz_types 없이 단독으로 보내면 PUT 핸들러가 자유 텍스트에서 biz_types 를
    // 다시 추정해 덮어쓴다 — 둘은 항상 같이 보낸다.
    if ('biz_types' in patch || 'biz_type' in patch) { apiPatch.biz_types = next.biz_types; apiPatch.biz_type = next.biz_type; }
    if ('company_scale' in patch) apiPatch.company_scale = next.company_scale;
    if ('is_startup' in patch) apiPatch.is_startup = next.is_startup;
    if ('ceo_birth' in patch) apiPatch.ceo_birth = next.ceo_birth || null;
    if ('employees' in patch) apiPatch.employees = next.employees.trim() !== '' ? Number(next.employees) : null;
    if ('sales_eok' in patch) apiPatch.sales_eok = next.sales_eok.trim() !== '' ? Number(next.sales_eok) : null;
    if ('established_date' in patch) apiPatch.established_date = next.established_date || null;
    if ('tags' in patch || 'arts' in patch) apiPatch.tags = mergeTags(otherTagsOf(company.tags), next);
    if ('sectors_off' in patch) apiPatch.sectors_off = next.sectors_off;   // 전용 대상 자동 판정 끄기 (2026-10-05f)
    // 2026-10-26 지도사 「누르면 바로 켜지는 게 아니라 깜빡깜빡하면서 켜져」 — 저장 전에 고른 값을 지워 옛 값이 잠깐 보였다(켜짐 → 꺼짐 → 켜짐)
    //   → 저장이 끝나 새 업체 값이 들어온 뒤에, 그사이 다시 바꾸지 않은 칸만 지운다
    let failed = false;
    try { await onUpdate(company.id, apiPatch as Partial<Company>); } catch { failed = true; onToast('저장 실패', 'error'); }
    const rest: Partial<MatchFieldsValue> = { ...matchPendingRef.current };
    (Object.keys(patch) as (keyof MatchFieldsValue)[]).forEach((k) => { if (failed || rest[k] === patch[k]) delete rest[k]; });
    matchPendingRef.current = rest; setMatchPending(rest);
  }
  function handleMatchChange(patch: Partial<MatchFieldsValue>) {
    const merged = { ...matchPendingRef.current, ...patch };
    matchPendingRef.current = merged;
    setMatchPending(merged);
    if (matchTimerRef.current) window.clearTimeout(matchTimerRef.current);
    matchTimerRef.current = window.setTimeout(flushMatch, 450);
  }

  const fp = fps.find(f => (company.fps || []).some(cf => cf.fp_id === f.id));
  const activeTasks = tasks.filter(t => t.company_id === company.id && t.status !== '완료');
  const allTasks = tasks.filter(t => t.company_id === company.id).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime());
  async function moveTask(idx: number, dir: -1 | 1) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= allTasks.length) return;
    const ids = allTasks.map(t => t.id);
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    try { await api.reorderTasks(company.id, ids); await onRefresh(); } catch { onToast('순서 변경 실패', 'error'); }
  }
  // V21_DND:2-Ba — 업무 드래그앤드롭
  const [taskDragIdx, setTaskDragIdx] = useState<number | null>(null);
  const [taskDragOver, setTaskDragOver] = useState<number | null>(null);
  const onTaskDrop = async (targetIdx: number) => {
    if (taskDragIdx === null || taskDragIdx === targetIdx) { setTaskDragIdx(null); setTaskDragOver(null); return; }
    const arr = allTasks.slice();
    const m = arr.splice(taskDragIdx, 1)[0];
    arr.splice(targetIdx, 0, m);
    setTaskDragIdx(null); setTaskDragOver(null);
    try { await api.reorderTasks(company.id, arr.map(t => t.id)); await onRefresh(); }
    catch { onToast('순서 변경 실패', 'error'); }
  };
  const compConsults = consultations.filter(c => inConsult(c, company.id)).sort((a, b) => b.date.localeCompare(a.date));

  // 추천 정부지원사업 — 엔진 판정(services/govMatch, 기본/비엄격 모드) 그대로. 불러오기 · 월별 · 종류별 필터 · 공고문 보기 · 링크 복사는 GovRecoList(공용) 안에 (2026-10-07)

  // A-6: load storage files
  async function loadStorage() {
    setStorageLoading(true);
    try {
      const [savedRes, collectRes] = await Promise.all([
        fetch(`/api/tm/companies/${company.id}/storage`, { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') } }).then(r => r.json()).catch(() => ({ data: [] })),
        fetch(`/api/projects?company_biz_no=${encodeURIComponent(company.biz_no || '')}`, { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') } }).then(r => r.json()).catch(() => ({ data: [] })),
      ]);
      setStorageFiles(savedRes.data || []);
      setCollectFiles(collectRes.data || []);
    } catch { /* ignore */ }
    finally { setStorageLoading(false); }
  }

  async function saveToStorage(file: CollectFile) {
    try {
      await fetch(`/api/tm/companies/${company.id}/storage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') },
        body: JSON.stringify({ s3Key: file.s3_key, fileName: file.file_name, source: 'collect' }),
      });
      onToast('파일이 저장되었습니다');
      loadStorage();
    } catch { onToast('파일 저장 실패', 'error'); }
  }

  // A-7: create task via TaskRegisterModal
  async function handleTaskRegDone(result: { decisions: TaskDecisionItem[] }) {
    try {
      let firstId = '';
      for (const d of result.decisions) {
        const taskName = d.mode === 'template' ? taskTemplates.find(t => t.id === d.templateId)?.name || '업무' : d.customName || '직접입력 업무';
        const taskIcon = d.mode === 'template' ? taskTemplates.find(t => t.id === d.templateId)?.icon || '📋' : d.customIcon;
        const made = await api.createTask({
          assigned_to: (d as { who?: string }).who || '',   // 담당(2026-10-18) — 비면 오재인
          company_id: company.id,
          template_id: d.mode === 'template' ? d.templateId : null,
          category_path: d.category_path || [],
          type: taskName, icon: taskIcon,
          status: d.taskStatus as 'FP요청대기' | '진행' | '완료',
          deadline: d.deadline || null,
          waiting_fp: d.taskStatus === 'FP요청대기', venture: api.ventureReq(d.venture),
          steps: d.steps.filter(s => s.name.trim()).map((s, i) => ({ name: s.name, type: s.type, sort_order: i, due: s.due || null, extras: (s as unknown as Record<string,unknown>).extras || {} })),
        } as Record<string, unknown>);
        if (!firstId && made && made.data && made.data.id) firstId = made.data.id;
      }
      setShowTaskModal(false);
      // 「신청 업무 만들기」로 연 등록이면 그 기다림을 끝낸다(남은 문자도 안 보냄) — 실패해도 업무는 그대로
      if (taskForWatch) {
        try { await api.doneGovWatch(taskForWatch.id, firstId); onToast(`업무를 등록하고 「${taskForWatch.name}」 알림을 끝냈어요`); }
        catch { onToast(`업무는 등록했어요 — 「${taskForWatch.name}」 알림은 직접 「그만 알림」을 눌러 주세요`, 'error'); }
        setTaskForWatch(null); setWatchReload(n => n + 1);
      } else onToast(`${result.decisions.length}건 업무가 등록되었습니다`);
      await onRefresh();
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '업무 등록 실패', 'error'); }
  }

  async function handleSaveInfo() {
    try {
      // tags는 이 폼에 편집 UI가 없다(관심분류·전용분야는 CompanyMatchFields, 그 외 태그도 여기선 안 건드림) — 보냈다간
      // company 마운트 시점의 오래된 스냅샷으로 매칭정보 블록의 방금 저장한 변경분을 덮어쓸 수 있어 제외한다.
      await onUpdate(company.id, { name: form.name, biz_no: form.biz_no, company_reg_num: form.company_reg_num, ceo: form.ceo, ceo_phone: form.ceo_phone, ceo_email: form.ceo_email, address: form.address, manager: form.manager, manager_phone: form.manager_phone, manager_email: form.manager_email,
        real_ceo: form.real_ceo || '', real_ceo_phone: form.real_ceo_phone || '',
        extra_managers: mgrs.filter(m => m.name.trim() || m.phone.trim() || m.email.trim()) });
      setEditInfo(false); onToast('정보가 저장되었습니다');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '저장 실패', 'error'); }
  }

  async function loadFinance() {
    const crno = (company.company_reg_num || '').replace(/[^0-9]/g, '');
    if (!crno) { onToast('법인등록번호가 없습니다', 'error'); return; }
    setFinanceLoading(true);
    try {
      const res = await api.getCompanyFinance(crno);
      setFinanceData(res.data || []);
    } catch { onToast('재무정보 조회 실패', 'error'); }
    finally { setFinanceLoading(false); }
  }

  const upd = (k: keyof typeof form, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  const TABS: [Tab, string][] = [['info', '기본정보'], ['tasks', `업무(${activeTasks.length})`], ['comm', '커뮤니케이션'], ['storage', '저장공간'], ['finance', '재무정보']];

  // 고객 페이지 링크 /c/<token> — 만들기 · 복사 · 열기 · 새로 만들기(옛 링크 닫힘, 한 번 더 누름) (2026-10-12)
  const clientUrl = clientLink.path ? window.location.origin + clientLink.path : '';
  const copyClient = (url: string) => {
    if (!navigator.clipboard) { onToast('주소를 길게 눌러 복사해 주세요'); return; }
    navigator.clipboard.writeText(url).then(() => onToast('고객 페이지 주소를 복사했어요')).catch(() => onToast('주소를 길게 눌러 복사해 주세요'));
  };
  const openClientLink = async (reissue: boolean) => {
    try {
      const r = await api.issueClientLink(company.id, reissue);
      if (!r.success || !r.data) { onToast(r.error || '링크를 만들지 못했어요', 'error'); return; }
      setClientLink({ path: r.data.path, open: true, confirm: false });
      copyClient(window.location.origin + r.data.path);
      if (reissue) onToast('새 링크를 만들었어요 · 옛 링크는 이제 열리지 않아요');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '링크를 만들지 못했어요', 'error'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {undoNode}
      {/* 헤더 */}
      <div style={{ borderBottom: `1px solid ${C.border}`, background: C.white, flexShrink: 0 }}>
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: T.lg, fontWeight: 800 }}>{editInfo ? (form.name || company.name) : company.name}</div>
              <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                {fp && <Badge bg={C.greenLt} color={C.green}>{fp.name} FP</Badge>}
                <StatusBadge status={(() => {
                  const myTasks = tasks.filter(t => t.company_id === company.id);
                  if (myTasks.some(t => t.status === '진행')) return '진행';
                  if (myTasks.length > 0 && myTasks.every(t => t.status === '완료')) return '완료';
                  return company.tm_status || '대기';
                })()} />
                {parseTags(company.tags).slice(0, 2).map(t => <Badge key={t}>{t}</Badge>)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => (clientLink.open ? setClientLink(p => ({ ...p, open: false, confirm: false })) : openClientLink(false))} title="고객 페이지 링크 — 로그인 없이 보는 진행 안내"
                style={btnStyle(C.primary, C.bgAlt)}>🔗 고객 페이지</button>
              {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: T.lg + 1 }}>✕</button>}
            </div>
          </div>
        </div>
        {clientLink.open && clientUrl && (
          <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, background: C.bgAlt, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.xs, color: C.sub, flex: 1, minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all' }}>{clientUrl}</span>
            <button onClick={() => copyClient(clientUrl)} style={btnStyle(C.accent, C.white)}>복사</button>
            <button onClick={() => window.open(clientUrl, '_blank', 'noopener')} style={btnStyle(C.accent, C.white)}>열기</button>
            {clientLink.confirm
              ? <>
                  <span style={{ fontSize: T.xs, color: C.red }}>옛 링크가 닫혀요</span>
                  <button onClick={() => openClientLink(true)} style={btnStyle(C.red, C.white)}>새로 만들기</button>
                  <button onClick={() => setClientLink(p => ({ ...p, confirm: false }))} style={btnStyle(C.muted, C.white)}>취소</button>
                </>
              : <button onClick={() => setClientLink(p => ({ ...p, confirm: true }))} title="링크가 다른 사람에게 갔을 때 — 새로 만들면 옛 링크는 닫혀요" style={btnStyle(C.muted, C.white)}>새로 만들기</button>}
          </div>
        )}
        {/* 탭 */}
        <div style={{ display: 'flex', borderTop: `1px solid ${C.border}` }}>
          {TABS.map(([k, lb]) => (
            <button key={k} onClick={() => { setTab(k); if (k === 'storage') loadStorage(); }}
              style={{ flex: 1, padding: '7px 0', fontSize: T.xs, fontWeight: tab === k ? 700 : 400, color: tab === k ? C.accent : C.muted, background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === k ? C.accent : 'transparent'}`, cursor: 'pointer' }}>{lb}</button>
          ))}
        </div>
      </div>

      {/* 콘텐츠 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>

        {/* ── 기본정보 ── */}
        {tab === 'info' && <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            {editInfo
              ? <><button onClick={handleSaveInfo} style={btnStyle(C.accent, C.accentLt)}>💾 저장</button><button onClick={() => { setEditInfo(false); setForm({ ...company }); setMgrs(extraManagersOf(company)); }} style={{ ...btnStyle(C.muted, C.bgAlt), marginLeft: 4 }}>취소</button></>
              : <button onClick={() => setEditInfo(true)} style={btnStyle(C.accent, C.accentLt)}>✏️ 정보 수정</button>}
          </div>
          {editInfo ? (
            <div style={{ marginBottom: 12 }}>
              {/* 업종·설립일은 아래 「정부지원사업 매칭」(CompanyMatchFields)이 다룬다 — 중복 제거 */}
              {([['회사명', 'name'], ['사업자번호', 'biz_no'], ['법인등록번호', 'company_reg_num'], ['대표자', 'ceo'], ['대표 연락처', 'ceo_phone'], ['대표 이메일', 'ceo_email'], ['실제 대표', 'real_ceo'], ['실제 대표 연락처', 'real_ceo_phone'], ['주소', 'address'], ['담당자', 'manager'], ['담당자 연락처', 'manager_phone'], ['담당자 이메일', 'manager_email']] as [string, keyof typeof form][]).map(([lb, key]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>{lb}</span>
                  <input value={(form[key] as string) || ''} onChange={e => upd(key, e.target.value)} placeholder={lb} style={{ flex: 1, padding: '5px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
                </div>
              ))}
              {/* 담당자 더 — 담당자 2, 3 … (이름 · 연락처 · 이메일 한 줄) */}
              {mgrs.map((m, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>담당자 {i + 2}</span>
                  <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr 1.3fr 20px', gap: 4, alignItems: 'center' }}>
                    {(['name', 'phone', 'email'] as const).map(k => (
                      <input key={k} value={m[k]} placeholder={k === 'name' ? '이름' : k === 'phone' ? '연락처' : '이메일'}
                        onChange={e => { const v = e.target.value; setMgrs(p => p.map((x, j) => (j === i ? { ...x, [k]: v } : x))); }}
                        style={{ minWidth: 0, padding: '5px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
                    ))}
                    <button type="button" aria-label={`담당자 ${i + 2} 빼기`} title="이 담당자 빼기" onClick={() => setMgrs(p => p.filter((_, j) => j !== i))}
                      style={{ border: 'none', background: 'none', color: C.muted, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                  </div>
                </div>
              ))}
              {mgrs.length < 20 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ minWidth: 82, flexShrink: 0 }} />
                  <button type="button" onClick={() => setMgrs(p => [...p, { name: '', phone: '', email: '' }])}
                    style={{ height: 26, padding: '0 10px', borderRadius: 13, border: `1px dashed ${C.accent}`, background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>+ 담당자 추가</button>
                </div>
              )}
              {/* 담당 FP (수정모드) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>담당 FP</span>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {(company.fps || []).map(cfp => {
                    const fpInfo = fps.find(f => f.id === cfp.fp_id);
                    return (
                      <span key={cfp.fp_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4, background: C.accentLt, color: C.accent, fontSize: T.xs, fontWeight: 600 }}>
                        {cfp.name}{fpInfo?.company ? ` (${fpInfo.company})` : ''}
                        <button onClick={() => removeFp(cfp.fp_id)} aria-label={`${cfp.name} 제거`} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: T.xs, padding: 0 }}>✕</button>
                      </span>
                    );
                  })}
                  {/* 이름을 치면 찾아 바로 추가 · 없으면 그 자리에서 새 FP 등록 후 추가 (2026-10-07 지도사) */}
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <PersonPicker kind="fp" size="sm" clearAfterPick people={fps} value="" exclude={curFpIds} placeholder="FP 이름으로 찾아 추가"
                      onChange={addFp} onToast={onToast} />
                  </div>
                </div>
              </div>
              {/* 담당 WM (수정모드) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0, alignSelf: 'flex-start', paddingTop: 8 }}>담당 WM</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <WmMultiPicker size="sm" people={wms} value={wmDraft || curWmIds} onChange={setWmList}
                    onCreated={w => setWms(prev => prev.some(x => x.id === w.id) ? prev : [...prev, w as WM])} onToast={onToast} />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              {([['회사명', company.name], ['사업자번호', company.biz_no], ['대표자', company.ceo], ['대표 연락처', company.ceo_phone], ['대표 이메일', company.ceo_email], ...((company.real_ceo || company.real_ceo_phone) ? ([['실제 대표', company.real_ceo], ['실제 대표 연락처', company.real_ceo_phone]] as [string, string | undefined][]) : []), ['주소', (company.address || '') + (company.region_detail ? ` (${company.region_detail})` : '')], ['설립일', company.established_date ? String(company.established_date).slice(0,10) + (company.established_year ? ` (${company.established_year}년)` : '') : (company.established_year ? String(company.established_year) + '년' : null)]] as [string, string | null | undefined][]).map(([lb, val]) => (
                <div key={lb} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>{lb}</span>
                  <span style={{ fontSize: T.xs, color: C.text }}>{val || '—'}</span>
                </div>
              ))}
              {/* 담당 FP */}
              <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
                <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>담당 FP</span>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {(company.fps || []).map(cfp => {
                    const fpInfo = fps.find(f => f.id === cfp.fp_id);
                    return (
                      <span key={cfp.fp_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: T.xs, color: C.text }}>
                        {cfp.name}{fpInfo?.company ? ` (${fpInfo.company})` : ''}
                        {/* 팀장님이 둔 구분(고객관리 · 2026-10-26) — 계약 고객일 때만 표시 */}
                        {cfp.stage === 'contract' && <span title="팀장님이 계약 고객으로 둔 업체" style={{ marginLeft: 2, padding: '0 5px', borderRadius: 4, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.sub }}>계약</span>}
                      </span>
                    );
                  })}
                  {/* 보기 화면은 표시만 — 추가 · 제거는 「정보 수정」의 PersonPicker 한 곳에서 (예전 이 자리 검색 칸은 editInfo 분기 밖이라 한 번도 보이지 않던 죽은 코드) */}
                  {(company.fps || []).length === 0 && <span style={{ fontSize: T.xs, color: C.muted }}>—</span>}
                </div>
              </div>
              {/* 담당 WM 여러 명(공동관리 · 2026-10-26) — 대표 먼저 · 백엔드가 company.wms 로 준다(옛 자료는 wm_name 한 분) */}
              {(() => {
                const list = (company.wms && company.wms.length) ? company.wms
                  : company.wm_id ? [{ wm_id: company.wm_id, name: company.wm_name || '', company: company.wm_company || '', phone: '' }] : [];
                return (
                  <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>담당 WM</span>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {list.length === 0 && <span style={{ fontSize: T.xs, color: C.muted }}>—</span>}
                      {list.map((w, i) => (
                        <span key={w.wm_id} style={{ fontSize: T.xs, color: C.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {w.name || '등록 정보 없음'}{w.company ? ` (${w.company})` : ''}
                          {list.length > 1 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: i === 0 ? C.accent : C.sub }}>{i === 0 ? '대표' : '함께'}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {([['담당자', company.manager], ['담당자 연락처', company.manager_phone], ['담당자 이메일', company.manager_email]] as [string, string | null | undefined][]).map(([lb, val]) => (
                <div key={lb as string} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>{lb}</span>
                  <span style={{ fontSize: T.xs, color: C.text }}>{val || '—'}</span>
                </div>
              ))}
              {extraManagersOf(company).map((m, i) => (
                <div key={'mgr' + i} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>담당자 {i + 2}</span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', columnGap: 10, fontSize: T.xs, color: C.text }}>
                    {[m.name, m.phone, m.email].filter(Boolean).map((v, k) => <span key={k} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{v}</span>)}
                  </span>
                </div>
              ))}
              {([['법인등록번호', company.company_reg_num], ['등록일', company.created_at?.slice(0, 10)]] as [string, string | null | undefined][]).map(([lb, val]) => (
                <div key={lb as string} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>{lb}</span>
                  <span style={{ fontSize: T.xs, color: C.text }}>{val || '—'}</span>
                </div>
              ))}
            </div>
          )}

          {/* A-2: 정부지원사업 매칭 — CompanyMatchFields 공용 컴포넌트(RegisterClientModal·ConsultResultModal과 동일).
              필드 변경마다 짧게 모아(디바운스) 즉시 저장 — 기존 즉시저장 UX 그대로. */}
          <div style={{ marginBottom: 12, padding: 10, borderRadius: R.md, background: C.bgAlt, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: T.xs, fontWeight: 700, color: C.text, marginBottom: 10 }}>🏛️ 정부지원사업 매칭</div>
            <FpFitLine companyId={company.id} refreshKey={company.updated_at} />
            <CompanyMatchFields value={matchValue} onChange={handleMatchChange} layout="compact" companyName={company.name}
              govAi={company.gov_ai} product={company.biz_type_detail || ''} entity={company.corp_type || ''} />
          </div>

          {/* A-3: 추천 정부지원사업 — 매칭 칸을 고쳐 저장하면(updated_at) 다시 판정 (2026-10-07 공용 GovRecoList) */}
          <GovRecoList companyId={company.id} refreshKey={company.updated_at} onNavigateGov={onNavigateGov} onToast={onToast}
            onWatch={p => { setWatchForm({ programId: p.id }); setTab('tasks'); }} />

          {/* 업체 삭제 */}
          <div style={{marginTop:32, paddingTop:16, borderTop:'1px solid '+C.border, textAlign:'right'}}>
            <button onClick={()=>setShowDeleteConfirm(true)} style={{fontSize:T.xs, padding:'6px 14px', border:'1px solid #DC2626', background:'white', color:'#DC2626', borderRadius:R.sm, cursor:'pointer'}}>🗑️ 업체 삭제</button>
          </div>
        </>}

        {/* ── 업무 ── */}
        {tab === 'tasks' && (() => {
          const stColor = (st: string) => st === '완료' ? C.green : st === 'FP요청대기' ? '#7C3AED' : st === '진행' ? C.accent : C.muted;
          const stBg = (st: string) => st === '완료' ? C.greenLt : st === 'FP요청대기' ? '#F3E8FF' : st === '진행' ? C.accentLt : '#F3F4F6';
          function fmtDl(d?: string|null) { if (!d) return ''; const s = d.slice(0,10); return `~${Number(s.slice(5,7))}/${Number(s.slice(8,10))}`; }
          function dlStyle(d?: string|null) {
            if (!d) return {};
            const _dt=new Date(d);_dt.setHours(0,0,0,0);const _dn=new Date();_dn.setHours(0,0,0,0);const diff=Math.round((_dt.getTime()-_dn.getTime())/86400000);
            if (diff < 0) return { background: '#FEE2E2', color: '#DC2626' };
            if (diff <= 7) return { background: '#FFF8E1', color: '#D97706' };
            return { background: '#F0F9FF', color: C.accent };
          }
          const nAct = allTasks.filter(t => t.status === '진행' || t.status === 'FP요청대기').length;
          const nHold = allTasks.filter(t => t.status === '보류').length;
          const nStop = allTasks.filter(t => t.status === '중단' && !isClosed(t)).length;
          const nClosed = allTasks.filter(isClosed).length;   // 이번 회차 마감(미선정) — 업체 포기 「중단」과 따로 셈
          const fpOne = fps.find(f => (company.fps || []).some(cf => cf.fp_id === f.id));
          return <>
          {allTasks.length > 0 && (
            <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 8 }}>
              업무 {allTasks.length}건 · 진행 {nAct}{nHold ? ` · 보류 ${nHold}` : ''}{nClosed ? ` · 이번 회차 마감 ${nClosed}` : ''}{nStop ? ` · 중단 ${nStop}` : ''}
            </div>
          )}

          {allTasks.map(t => {
            const isExp = expandTask === t.id;
            const isDone = t.status === '완료';
            const steps = t.steps || [];
            const doneSteps = steps.filter(s => s.status === '완료').length;
            // 중단 · 보류 = 접힌 회색 카드 (2026-10-12 · 4차 시안) — 누르면 펼침 · 「다시 진행」
            if ((t.status === '중단' || t.status === '보류') && !isExp) {
              const stop = t.status === '중단';
              const closed = isClosed(t);
              if (closed) {
                // 이번 회차 마감 — 회청색 · 결과 한 줄 · 「다음 회차 준비」(같은 업무 새로) · 「결과 취소」(이의신청 등 → 그 단계부터 다시)
                return (
                  <div key={t.id} onClick={() => setExpandTask(t.id)} style={{ borderRadius: 10, border: '1px solid #CBD5E1', background: '#F8FAFC', marginBottom: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.sub, flexShrink: 0 }}>{t.icon || '📋'} {t.type}</span>
                    <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#475569', color: '#fff', flexShrink: 0 }}>이번 회차 마감</span>
                    <span style={{ fontSize: T.xs, color: '#334155', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(t.stop_reason || '').replace(CLOSE_PREFIX, '').replace(/^ · /, '')}{t.stopped_at ? ' · ' + flowMd(String(t.stopped_at).slice(0, 10)) : ''}
                    </span>
                    <button onClick={async e => { e.stopPropagation(); if (await nextRound(t)) { await onRefresh(); onToast('다음 회차 업무를 만들었어요 · 이번 회차는 기록으로 남아요', 'success'); } else onToast('만들지 못했어요', 'error'); }}
                      style={{ flexShrink: 0, height: 28, padding: '0 10px', borderRadius: R.sm, border: '1px solid #475569', background: '#fff', color: '#334155', fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>다음 회차 준비</button>
                    <button onClick={e => { e.stopPropagation(); void flowRun(t, { action: 'restart' }, company.name); }}
                      style={{ flexShrink: 0, height: 28, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>결과 취소</button>
                  </div>
                );
              }
              return (
                <div key={t.id} onClick={() => setExpandTask(t.id)} style={{ borderRadius: 10, border: stop ? `0.5px solid ${C.border}` : `1px dashed ${C.borderDk}`, background: stop ? '#F9FAFB' : '#fff', marginBottom: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.sub }}>{t.icon || '📋'} {t.type}</span>
                  <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: stop ? '#4E5968' : '#fff', color: stop ? '#fff' : C.sub, border: stop ? 'none' : `1px dashed ${C.borderDk}` }}>{t.status}</span>
                  <span style={{ fontSize: T.xs, color: C.muted, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stop ? `${t.stop_reason || ''}${t.stopped_at ? ' · ' + flowMd(String(t.stopped_at).slice(0, 10)) + ' 중단' : ''} · ` : '잠시 멈춤 · '}{doneSteps}/{steps.length} 단계까지 진행
                  </span>
                  <button onClick={e => { e.stopPropagation(); void flowRun(t, { action: 'restart' }, company.name); }}
                    style={{ flexShrink: 0, height: 28, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>↻ 다시 진행</button>
                </div>
              );
            }
            return (
              <div key={t.id} style={{ borderRadius: 10, border: `0.5px solid ${isExp ? C.accent : C.border}`, background: '#fff', marginBottom: 8, overflow: 'hidden', opacity: isDone && !isExp ? 0.6 : 1 }}>
                {/* 헤더 */}
                <div onClick={() => setExpandTask(isExp ? null : t.id)} style={{ padding: '10px 14px', cursor: 'pointer', background: isExp ? '#F8FAFC' : 'transparent', borderBottom: isExp ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    {/* V21_DND:2-Bb — drag handle */}
                    <span draggable onDragStart={(e)=>{e.stopPropagation();setTaskDragIdx(allTasks.indexOf(t));e.dataTransfer.effectAllowed='move';}} onDragEnter={(e)=>{const ix=allTasks.indexOf(t);if(taskDragIdx!==null&&taskDragIdx!==ix)setTaskDragOver(ix);}} onDragOver={(e)=>{e.preventDefault();e.dataTransfer.dropEffect='move';}} onDrop={(e)=>{e.preventDefault();e.stopPropagation();onTaskDrop(allTasks.indexOf(t));}} onDragEnd={()=>{setTaskDragIdx(null);setTaskDragOver(null);}} title="드래그하여 순서 변경" style={{cursor:'grab',fontSize:11,color:'#9CA3AF',padding:'0 2px',marginRight:4,lineHeight:1,userSelect:'none',flexShrink:0,opacity:taskDragIdx===allTasks.indexOf(t)?0.4:(taskDragOver===allTasks.indexOf(t)?1:0.7)}}>⋮⋮</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                      {editingTitle===t.id ? (
                        <input type='text' defaultValue={t.type} autoFocus
                          onClick={e=>e.stopPropagation()}
                          onBlur={async(e)=>{const v=e.target.value.trim();setEditingTitle(null);if(v&&v!==t.type){try{await api.updateTask(t.id,{type:v});await onRefresh();onToast('업무명이 수정되었습니다');}catch{onToast('업무명 수정 실패','error');}}}}
                          onKeyDown={e=>{if(e.key==='Enter')(e.target as HTMLInputElement).blur();if(e.key==='Escape')setEditingTitle(null);}}
                          style={{fontSize:13,fontWeight:600,padding:'2px 6px',borderRadius:4,border:'1px solid #2E75B6',outline:'none',flex:1,minWidth:0}} />
                      ) : (
                        <span onClick={e=>{e.stopPropagation();setEditingTitle(t.id);}}
                          title='클릭하여 업무명 수정'
                          style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }}>{t.icon || '📋'} {t.type}</span>
                      )}
                      {steps.length > 0 && <span style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{doneSteps}/{steps.length}</span>}
                      {editingDeadline===t.id ? (
                        <input type='date' defaultValue={t.deadline?.slice(0,10)||''} autoFocus
                          onClick={e=>e.stopPropagation()}
                          onBlur={async(e)=>{const v=e.target.value;setEditingDeadline(null);if(v!==(t.deadline?.slice(0,10)||'')){try{await api.updateTask(t.id,{deadline:v||null});await onRefresh();onToast('기한이 수정되었습니다');}catch{onToast('기한 수정 실패','error');}}}}
                          onKeyDown={e=>{if(e.key==='Enter')(e.target as HTMLInputElement).blur();if(e.key==='Escape')setEditingDeadline(null);}}
                          style={{fontSize:10,padding:'2px 6px',borderRadius:6,border:'1px solid #2E75B6',outline:'none',flexShrink:0,width:120}} />
                      ) : (
                        <span onClick={e=>{e.stopPropagation();setEditingDeadline(t.id);}}
                          title='클릭하여 기한 수정'
                          style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, flexShrink: 0, cursor:'pointer', ...(t.deadline?dlStyle(t.deadline):{background:'#F3F4F6',color:'#9CA3AF'}) }}>{t.deadline ? fmtDl(t.deadline) : '기한 설정'}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {/* 담당(2026-10-18) — 비면 오재인 · 대시보드 · 빠진 입력 · 아침 문자가 담당별로 */}
                      <select value={whoOf(t)} title="담당" aria-label="담당" onClick={e => e.stopPropagation()}
                        onChange={async e => { const v = e.target.value; try { await api.updateTask(t.id, { assigned_to: v }); await onRefresh(); onToast(`담당을 ${v}(으)로 바꿨어요`, 'success'); } catch { onToast('담당을 바꾸지 못했어요', 'error'); } }}
                        style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4, fontWeight: 600, background: '#F3F4F6', color: '#374151', border: 'none', cursor: 'pointer', outline: 'none' }}>
                        {STAFF.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <select value={t.status} onClick={e => e.stopPropagation()}
                        onChange={async (e) => {
                          const status = e.target.value;
                          // 2026-10-12 — 중단은 이유 하나 고르기 · 보류 · 다시 진행은 진행 규칙(되돌리기 10초)으로
                          if (status === '중단') { setStopFor(t.id); return; }
                          if (status === '보류') { void flowRun(t, { action: 'hold' }, company.name); return; }
                          if (status === '진행' && (t.status === '중단' || t.status === '보류')) { void flowRun(t, { action: 'restart' }, company.name); return; }
                          try {
                            await api.updateTask(t.id, { status: status as Task['status'] });
                            if (status === 'FP요청대기') {
                              const allS = t.steps || [];
                              await Promise.all(allS.map(s => api.updateStep(t.id, s.id, { status: '대기', due: null } as Record<string, unknown>)));
                              onToast(allS.length ? `FP요청대기 · 하위 ${allS.length}단계 대기 전환 + 기한 삭제` : 'FP요청대기로 변경됨', 'success');
                            } else { onToast('업무 상태 변경됨', 'success'); }
                            await onRefresh();
                          } catch { onToast('상태 변경 실패', 'error'); }
                        }}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: stBg(t.status), color: stColor(t.status), border: 'none', cursor: 'pointer', outline: 'none' }}>
                        <option value="진행">진행</option>
                        <option value="완료">완료</option>
                        <option value="FP요청대기">FP요청대기</option>
                        <option value="대기">대기</option>
                        <option value="보류">보류</option>
                        <option value="중단">중단</option>
                      </select>
                      <span style={{ fontSize: 11, color: C.muted, transition: 'transform 0.2s', transform: isExp ? 'rotate(90deg)' : 'none' }}>▶</span>
                    </div>
                  </div>
                  {steps.length > 0 && !isDone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 3, borderRadius: 2, background: '#E5E7EB', overflow: 'hidden' }}>
                        <div style={{ height: 3, borderRadius: 2, width: `${steps.length > 0 ? Math.round(doneSteps/steps.length*100) : 0}%`, background: t.status === '완료' ? '#0F6E56' : '#185FA5', transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 500, color: '#185FA5', flexShrink: 0 }}>{steps.length > 0 ? Math.round(doneSteps/steps.length*100) : 0}%</span>
                    </div>
                  )}
                </div>
                {/* 업무 중단 이유 (2026-10-12) */}
                {stopFor === t.id && (
                  <div onClick={e => e.stopPropagation()} style={{ padding: '10px 14px', background: '#FEF2F2', borderTop: '1px solid #FCA5A5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <b style={{ fontSize: T.sm, color: C.red }}>✕ 업무 중단</b>
                      <span style={{ fontSize: T.sm, color: C.sub }}>업체가 안 하기로 했나요? 이유를 누르면 바로 중단돼요</span>
                      <button onClick={() => setStopFor(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: C.accent, fontSize: T.sm, cursor: 'pointer' }}>취소</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {STOP_REASONS.map(r => <button key={r} onClick={() => { setStopFor(null); void flowRun(t, { action: 'stop', reason: r }, company.name); }} style={{ height: 32, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>{r}</button>)}
                      <input value={stopOther} onChange={e => setStopOther(e.target.value)} placeholder="기타 (직접 쓰기)" style={{ width: 150, height: 32, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm }} />
                      {stopOther.trim() && <button onClick={() => { const r = stopOther.trim(); setStopFor(null); setStopOther(''); void flowRun(t, { action: 'stop', reason: r }, company.name); }} style={{ height: 32, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>중단</button>}
                    </div>
                    <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6 }}>남은 단계는 목록에서 빠지고 기록은 남아요 · FP 포털엔 「중단」 · 언제든 「다시 진행」</div>
                  </div>
                )}
                {/* 지금 단계 막대 — 완료 → 다음 · 미루기 · 회신 대기 (진행 · FP요청대기 업무) */}
                {(t.status === '진행' || t.status === 'FP요청대기') && stopFor !== t.id && (
                  <NowBar task={t} comp={company} run={flowRun} fpPhone={fpOne?.phone} fpName={fpOne?.name} />
                )}
                {/* 단계 상세 */}
                {isExp && (
                  <div style={{ borderTop: `1px solid ${C.border}` }}>
                    <div style={{ padding: '10px 14px 8px 16px' }}>
                      <TaskStepDetail task={t} company={company} aiPresets={aiPresets} onRefresh={() => { onRefresh().catch(console.error); }} onToast={onToast} hideHeader />
                    </div>
                    <div style={{ padding: '6px 16px 8px', borderTop: `1px solid ${C.border}`, textAlign: 'right' }}>
                      <button onClick={async()=>{if(!window.confirm('이 업무를 삭제하시겠습니까?'))return;try{await api.deleteTask(t.id);await onRefresh();onToast('업무가 삭제되었습니다');}catch{onToast('업무 삭제 실패','error');}}}
                        style={{ fontSize: T.xs, padding: '4px 12px', border: '1px solid #DC262644', background: 'transparent', color: '#DC2626', borderRadius: R.sm, cursor: 'pointer' }}>🗑️ 삭제</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* 기다리는 공고(공고 나오면 알림 · 2026-10-30) — 기다림이 있거나 입력 중일 때만 */}
          <GovWatchSection companyId={company.id} form={watchForm} onCloseForm={() => setWatchForm(null)} focusId={focusWatch} reloadKey={watchReload} onToast={onToast}
            onMakeTask={w => { setTaskForWatch({ id: w.id, name: w.name }); setShowTaskModal('template'); }} />

          {/* 업무 추가 — 컴팩트 디자인 */}
          <div style={{ marginTop: allTasks.length > 0 ? 12 : 0 }}>
            {!showTaskModal ? (
              <>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.3 }}>업무 추가</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div onClick={() => setShowTaskModal('template')}
                    style={{ flex: 1, padding: '12px 16px', borderRadius: 8, border: `1.5px dashed ${C.accent}66`, background: `${C.accent}12`, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.accent; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${C.accent}44`; }}>
                    <span style={{ fontSize: 14 }}>📋</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>템플릿에서 선택</span>
                  </div>
                  <div onClick={() => setShowTaskModal('custom')}
                    style={{ flex: 1, padding: '12px 16px', borderRadius: 8, border: `1.5px dashed ${C.sub}44`, background: '#F3F4F6', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.sub; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = C.border; }}>
                    <span style={{ fontSize: 14 }}>✏️</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.sub }}>직접 입력</span>
                  </div>
                  <div onClick={() => setWatchForm(f => f || {})} title="신청하기로 한 사업의 공고가 나오면 화면 · 문자로 알려요"
                    style={{ flex: 1, padding: '12px 16px', borderRadius: 8, border: `1.5px dashed ${C.green}55`, background: C.greenLt, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.green; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${C.green}55`; }}>
                    <span style={{ fontSize: 14 }}>📣</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.green }}>공고 나오면 알림</span>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', background: '#fff' }}>
                {taskForWatch && (
                  <div style={{ padding: '8px 14px', background: C.greenLt, borderBottom: `1px solid ${C.border}`, fontSize: T.xs, color: '#166534', fontWeight: 600 }}>
                    📣 「{taskForWatch.name}」 신청 업무 — 등록하면 이 공고 알림은 끝나요(템플릿을 고르거나 직접 입력)
                  </div>
                )}
                <TaskRegisterModal title="업무 등록" companyId={company.id} companyName={company.name} templates={taskTemplates} taskCategories={taskCategories} aiPresets={aiPresets}
                  onDone={handleTaskRegDone} onCancel={() => { setShowTaskModal(false); setTaskForWatch(null); }} onToast={(msg) => onToast(msg)} panelMode initialMode={showTaskModal || undefined}
                  headerExtra={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={() => { setShowTaskModal(false); setTaskForWatch(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.muted, padding: '2px 6px' }}>✕ 닫기</button></div>} />
              </div>
            )}
          </div>
        </>;
        })()}

        {/* ── 기본정보 하단: 연결한 법령·판례·해석 ── */}
        {tab === 'info' && <CompanyLegalSection companyId={company.id} onError={m => onToast(m, 'error')} />}
        {/* ── 커뮤니케이션 ── */}
        {/* 메모 · 통화 녹음 · 보낸 문자 — 같은 머리 모양 · 같은 간격(2026-10-28 지도사 「위에는 비고 밑에 갑자기 통화 녹음이」) */}
        {tab === 'comm' && <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}><b style={{ fontSize: T.md, color: C.text }}>메모</b><span style={{ fontSize: T.xs, color: C.muted }}>내 메모 · 체크하면 FP 포털 · 고객 페이지에도 보여요</span></div>}
        {tab === 'comm' && <CommentSection companyId={company.id} onToast={onToast} />}
        {/* 고객 통화 녹음(2026-10-28) — 대화 글 · 담당 팀장님께 문자 */}
        {tab === 'comm' && <CallRecordings key={company.id} companyId={company.id} companyName={company.name} onToast={onToast} />}
        {/* 보낸 문자 — 대표님 · 팀장님 · WM 께 전산에서 나간 것(2026-10-28 지도사 「뭐라고 나갔는지 알아야지」) */}
        {tab === 'comm' && <CompanySmsLog key={'sms-' + company.id} companyId={company.id} />}

        {/* ── 저장공간 ── */}
        {tab === 'storage' && (
          <div>
            {editConsult && <ConsultEditModal consultation={editConsult} wms={wms} fps={fps} onDone={async () => { setEditConsult(null); await onRefresh(); onToast('상담 이력 수정됨'); }} onCancel={() => setEditConsult(null)} onToast={onToast} />}
            {resultConsult && <ConsultResultModal consultation={resultConsult} templates={taskTemplates} taskCategories={taskCategories} aiPresets={aiPresets} fps={fps} wms={wms} existingTasks={tasks.filter(t => t.consult_id === resultConsult.id)} onDone={async () => { setResultConsult(null); await onRefresh(); onToast('상담결과 수정됨'); }} onCancel={() => setResultConsult(null)} onToast={onToast} />}
            {/* 상담 이력 */}
            <div style={{ fontSize: T.sm, fontWeight: 700, marginBottom: 6 }}>상담 이력 ({compConsults.length}건)</div>
            {compConsults.length === 0 && <div style={{ padding: 12, textAlign: 'center', fontSize: T.xs, color: C.muted }}>상담 이력 없음</div>}
            {compConsults.slice(0, 5).map(c => (
              <div key={c.id} style={{ padding: '8px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, marginBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 6 }}>
                  <span style={{ fontSize: T.xs, fontWeight: 600 }}>{(c.date||'').slice(0,10)} {(c.time||'').replace(/:\d{2}\.\d{3}Z$/,'')}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Badge>{c.status}</Badge>
                    <button onClick={() => { if (c.status === 'completed' || c.status === 'sent') setResultConsult(c); else setEditConsult(c); }} style={{ padding: '2px 7px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.white, color: C.accent, fontSize: 10, cursor: 'pointer' }}>수정</button>
                    {/* V20_MASTER:10 — 상담이력 삭제 버튼 */}
                    <button onClick={async () => {
                      if (!window.confirm('이 상담 이력을 삭제할까요?')) return;
                      try { await api.deleteConsultation(c.id); await onRefresh(); onToast('상담 이력 삭제됨'); }
                      catch { onToast('삭제 실패', 'error'); }
                    }} style={{ padding: '2px 7px', borderRadius: R.sm, border: `1px solid ${C.red}`, background: C.white, color: C.red, fontSize: 10, cursor: 'pointer' }}>삭제</button>
                  </span>
                </div>
                {c.location && <div style={{ fontSize: T.xs, color: C.muted }}>{c.location}</div>}
                {/* 상담 내용 전문 + 첨부파일 (2026-08-15 사용자 요청) */}
                {c.content && <div style={{ fontSize: T.xs, color: C.sub, marginTop: 4, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.content}</div>}
                {(() => {
                  const atts = [...(Array.isArray(c.files) ? c.files : []), ...(Array.isArray(c.ref_files) ? c.ref_files : [])];
                  if (atts.length === 0) return null;
                  return (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.border}` }}>
                      {atts.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: i === 0 ? 0 : 4 }}>
                          <span style={{ fontSize: 11 }}>📄</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: T.xs, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <button onClick={async () => {
                            if (!f.s3_key) { onToast('파일 정보가 없습니다', 'error'); return; }
                            try {
                              const r = await fetch('/api/tm/download?key=' + encodeURIComponent(f.s3_key) + '&fileName=' + encodeURIComponent(f.name), { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') } });
                              const d = await r.json();
                              if (d.data?.url) window.open(d.data.url, '_blank'); else onToast('다운로드 실패', 'error');
                            } catch { onToast('다운로드 실패', 'error'); }
                          }} style={{ flexShrink: 0, fontSize: 10, padding: '2px 8px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer', fontWeight: 600 }}>다운로드</button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ))}

            {/* A-6: Collect 파일 + 서버 저장 */}
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: T.sm, fontWeight: 700 }}>💾 저장된 파일 ({storageFiles.length}건)</div>
                <button onClick={loadStorage} disabled={storageLoading}
                  style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer', fontWeight: 600 }}>
                  {storageLoading ? '조회 중...' : '새로고침'}
                </button>
              </div>
              {storageFiles.length === 0 && !storageLoading && <div style={{ padding: 20, textAlign: "center", fontSize: T.xs, color: C.muted }}>자료수집 모듈에서 파일을 저장하면 여기에 표시됩니다</div>}
            </div>

            {/* 저장된 파일 목록 */}
            {storageFiles.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: T.sm, fontWeight: 700, marginBottom: 6 }}>저장된 파일</div>
                {storageFiles.map(f => {
                  const displayName = f.item_title || f.file_name;
                  const dateStr = f.uploaded_at || f.created_at || '';
                  const dateDisplay = dateStr ? dateStr.slice(0, 16).replace('T', ' ') : '';
                  return (
                  <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.bgAlt, marginBottom: 4, gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div onClick={async () => { try { const r = await fetch('/api/tm/preview?key=' + encodeURIComponent(f.s3_key) + '&name=' + encodeURIComponent(f.file_name || ''), { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') } }); const d = await r.json(); if (d.data?.url) { const ext = (f.file_name||'').toLowerCase(); const isImg = ext.endsWith('.jpg')||ext.endsWith('.jpeg')||ext.endsWith('.png')||ext.endsWith('.gif'); setPreviewFile({url:d.data.url,name:f.file_name,isImg}); } } catch {} }} style={{ fontSize: T.xs, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: C.accent, textDecoration: 'underline' }}>{displayName}</div>
                      {f.item_title && f.file_name !== f.item_title && <div style={{ fontSize: 10, color: C.sub, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</div>}
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{dateDisplay}{f.source ? ` · ${f.source}` : ''}{f.sensitive ? <span style={{ marginLeft: 6, fontSize: 9, color: '#D97706', fontWeight: 700, background: '#FFFBEB', padding: '1px 5px', borderRadius: 3 }}>민감 · FP 비공개</span> : null}</div>
                    </div>
                    <button onClick={async () => { try { const r = await fetch('/api/tm/download?key=' + encodeURIComponent(f.s3_key) + '&fileName=' + encodeURIComponent(f.file_name), { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') } }); const d = await r.json(); if (d.data?.url) window.open(d.data.url, '_blank'); else onToast('다운로드 실패', 'error'); } catch { onToast('다운로드 실패', 'error'); } }}
                      style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>다운로드</button>
                    <button onClick={async () => { if (!confirm('삭제하시겠습니까?')) return; try { await fetch('/api/tm/companies/' + company.id + '/storage/' + f.id, { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') } }); onToast('삭제됨'); loadStorage(); } catch { onToast('삭제 실패', 'error'); } }}
                      style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.red}`, background: C.redLt, color: C.red, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>삭제</button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── 재무정보 ── */}
        {tab === 'finance' && (
          <FinanceTab
            company={company}
            financeData={financeData}
            financeLoading={financeLoading}
            onLoad={loadFinance}
            onReload={() => { setFinanceData(null); loadFinance(); }}
            onSaveCrno={async (crno) => {
              try {
                await onUpdate(company.id, { company_reg_num: crno });
                onToast('법인등록번호가 저장되었습니다');
              } catch { onToast('저장 실패', 'error'); }
            }}
          />
        )}
      </div>
      {previewFile && (
        <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.7)',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setPreviewFile(null)}>
          <div style={{background:'#fff',borderRadius:12,maxWidth:'90vw',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 16px',borderBottom:'1px solid #E5E7EB'}}>
              <span style={{fontSize:T.md,fontWeight:600}}>{previewFile.name}</span>
              <button onClick={()=>setPreviewFile(null)} style={{background:'none',border:'none',fontSize:T.xl,cursor:'pointer'}}>✕</button>
            </div>
            <div style={{overflow:'auto',maxHeight:'80vh',padding:8}}>
              {previewFile.isImg ? <img src={previewFile.url} alt="" style={{maxWidth:'80vw',maxHeight:'75vh',objectFit:'contain'}} /> : <iframe src={previewFile.url} title="Preview" style={{width:'80vw',height:'75vh',border:'none'}} />}
            </div>
          </div>
        </div>
      )}
            {showDeleteConfirm && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div onClick={()=>setShowDeleteConfirm(false)} style={{position:'absolute',inset:0,background:'rgba(17,24,39,0.4)',backdropFilter:'blur(2px)'}}/>
          <div style={{position:'relative',width:'100%',maxWidth:420,background:'#fff',borderRadius:12,padding:24,boxShadow:'0 20px 60px rgba(0,0,0,0.15)'}}>
            <div style={{fontSize:T.lg,fontWeight:700,marginBottom:12}}>⚠️ 업체 삭제 확인</div>
            <div style={{fontSize:T.md,color:C.text,marginBottom:12}}>{company.name}을 삭제하면 아래 데이터가 함께 삭제됩니다:</div>
            <div style={{fontSize:T.sm,color:C.sub,lineHeight:2,marginBottom:12}}>
              📋 업무 {tasks.filter(t=>t.company_id===company.id).length}건<br/>
              👤 담당 FP 연결 {(company.fps||[]).length}명 (FP 자체는 삭제 안됨)<br/>
              📁 저장된 파일 및 자료수집 이력
            </div>
            <div style={{fontSize:T.md,color:'#DC2626',fontWeight:700,marginBottom:16}}>이 작업은 되돌릴 수 없습니다.</div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setShowDeleteConfirm(false)} style={{padding:'8px 16px',borderRadius:R.md,border:'1px solid '+C.border,background:'#fff',fontSize:T.sm,cursor:'pointer'}}>취소</button>
              <button onClick={async()=>{try{await api.deleteCompany(company.id);await onRefresh();if(onClose)onClose();onToast('업체가 삭제되었습니다');}catch(e){onToast(e instanceof Error?e.message:'삭제 실패','error');}setShowDeleteConfirm(false);}} style={{padding:'8px 16px',borderRadius:R.md,border:'none',background:'#DC2626',color:'#fff',fontSize:T.sm,fontWeight:700,cursor:'pointer'}}>영구 삭제하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function btnStyle(color: string, bg: string) {
  return { fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${color}`, background: bg, color, cursor: 'pointer', fontWeight: 600 } as const;
}

function InfoGrid({ rows }: { rows: [string, string | null | undefined][] }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {rows.map(([lb, val]) => (
        <div key={lb} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: T.xs, color: C.muted, minWidth: 82, flexShrink: 0 }}>{lb}</span>
          <span style={{ fontSize: T.xs, color: C.text }}>{val || '—'}</span>
        </div>
      ))}
    </div>
  );
}

function fmtEok(n: number | undefined | null): string {
  if (n == null || n === 0) return '—';
  return (n / 100000000).toLocaleString('ko-KR', { maximumFractionDigits: 1 }) + '억';
}

function fmtPct(n: number | undefined | null): string {
  if (n == null || !isFinite(n)) return '—';
  return n.toLocaleString('ko-KR', { maximumFractionDigits: 1 }) + '%';
}

// ── 재무정보 탭 서브컴포넌트 ──
interface FinanceTabProps {
  company: { company_reg_num: string; biz_no: string };
  financeData: import('../api').FinanceData[] | null;
  financeLoading: boolean;
  onLoad: () => void;
  onReload: () => void;
  onSaveCrno: (crno: string) => Promise<void>;
}

function FinanceTab({ company, financeData, financeLoading, onLoad, onReload, onSaveCrno }: FinanceTabProps) {
  const [crnoInput, setCrnoInput] = useState(company.company_reg_num || '');
  const [saving, setSaving] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [showConsolidated, setShowConsolidated] = useState(true);
  const hasCrno = !!(company.company_reg_num || '').replace(/[^0-9]/g, '');

  async function handleSaveAndLoad() {
    const cleaned = crnoInput.replace(/[^0-9]/g, '');
    if (cleaned.length !== 13) { alert('법인등록번호는 13자리입니다'); return; }
    setSaving(true);
    await onSaveCrno(cleaned);
    setSaving(false);
    setTimeout(onLoad, 300);
  }

  // 법인등록번호 없는 경우
  if (!hasCrno) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ padding: '12px 14px', borderRadius: R.md, background: '#FEF3C7', border: '1px solid #FDE68A', marginBottom: 16, fontSize: T.xs, color: '#92400E', lineHeight: 1.7 }}>
          <strong>법인등록번호</strong>가 없습니다.<br />
          팝빌 조회(기본정보 탭) 시 자동 입력되며, 아래에서 직접 입력할 수도 있습니다.
        </div>
        <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 }}>법인등록번호 직접 입력 (13자리)</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={crnoInput}
            onChange={e => setCrnoInput(e.target.value.replace(/[^0-9]/g, '').slice(0, 13))}
            placeholder="1101110000000"
            maxLength={13}
            style={{ flex: 1, padding: '8px 10px', borderRadius: R.md, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', fontFamily: 'monospace' }}
          />
          <button
            onClick={handleSaveAndLoad}
            disabled={saving || crnoInput.replace(/[^0-9]/g, '').length !== 13}
            style={{ padding: '8px 16px', borderRadius: R.md, border: 'none', background: crnoInput.replace(/[^0-9]/g, '').length === 13 ? C.accent : C.borderDk, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>
            {saving ? '...' : '저장 후 조회'}
          </button>
        </div>
      </div>
    );
  }

  if (!financeData && !financeLoading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 4 }}>법인등록번호</div>
        <div style={{ fontSize: T.sm, fontWeight: 700, fontFamily: 'monospace', marginBottom: 14 }}>{company.company_reg_num}</div>
        <button onClick={onLoad} style={{ padding: '9px 24px', borderRadius: R.md, border: 'none', background: C.accent, color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
          재무정보 조회
        </button>
      </div>
    );
  }

  if (financeLoading) {
    return <div style={{ padding: 20, textAlign: 'center', color: C.muted }}>재무정보 조회 중...</div>;
  }

  if (!financeData || financeData.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
        <div style={{ fontSize: T.sm, color: C.muted, marginBottom: 12 }}>재무정보가 없습니다</div>
        <button onClick={onReload} style={{ padding: '6px 14px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer', color: C.muted }}>
          다시 조회
        </button>
      </div>
    );
  }

  // Group data by year
  const yearMap = new Map<string, typeof financeData>();
  financeData.forEach(d => {
    const list = yearMap.get(d.year) || [];
    list.push(d);
    yearMap.set(d.year, list);
  });
  const years = Array.from(yearMap.keys()).sort((a, b) => b.localeCompare(a));
  const activeYear = selectedYear && years.includes(selectedYear) ? selectedYear : years[0];

  // For the active year, pick consolidated (110) vs separate
  const yearEntries = yearMap.get(activeYear) || [];
  const consolidated = yearEntries.find(d => d.fnclDcd === '110');
  const separate = yearEntries.find(d => d.fnclDcd !== '110');
  const hasBoth = !!consolidated && !!separate;
  const activeData = (showConsolidated && consolidated) ? consolidated : (separate || consolidated || yearEntries[0]);

  if (!activeData) return null;

  const revenue = activeData.revenue || 0;
  const opProfit = activeData.operatingProfit || 0;
  const netInc = activeData.netIncome || 0;
  const opMargin = revenue ? (opProfit / revenue) * 100 : null;
  const debtRatio = activeData.equity ? (activeData.totalDebt / activeData.equity) * 100 : null;
  const currentRatio = activeData.currentLiabilities ? ((activeData.currentAssets || 0) / activeData.currentLiabilities) * 100 : null;

  const negStyle = (v: number | null | undefined): React.CSSProperties => (v != null && v < 0) ? { color: C.red } : {};
  const debtColor = (v: number | null): React.CSSProperties => {
    if (v == null) return {};
    if (v > 200) return { color: C.red, fontWeight: 700 };
    if (v <= 100) return { color: C.green, fontWeight: 700 };
    return {};
  };

  const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${C.border}` };
  const labelStyle: React.CSSProperties = { fontSize: T.xs, color: C.muted };
  const valStyle: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.text };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: T.sm, fontWeight: 700 }}>재무현황</div>
        <button onClick={onReload} style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', color: C.muted }}>새로고침</button>
      </div>

      {/* Year tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {years.map(y => (
          <button key={y} onClick={() => setSelectedYear(y)}
            style={{ padding: '5px 12px', borderRadius: R.sm, border: `1px solid ${y === activeYear ? C.accent : C.border}`, background: y === activeYear ? C.accent : C.white, color: y === activeYear ? '#fff' : C.sub, fontSize: T.xs, fontWeight: y === activeYear ? 700 : 400, cursor: 'pointer' }}>
            {y}
          </button>
        ))}
      </div>

      {/* Consolidated / Separate toggle */}
      {hasBoth && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
          <button onClick={() => setShowConsolidated(true)}
            style={{ flex: 1, padding: '5px 0', borderRadius: R.sm, border: `1px solid ${showConsolidated ? C.accent : C.border}`, background: showConsolidated ? C.accentLt : C.white, color: showConsolidated ? C.accent : C.muted, fontSize: T.xs, fontWeight: showConsolidated ? 700 : 400, cursor: 'pointer' }}>
            연결재무제표
          </button>
          <button onClick={() => setShowConsolidated(false)}
            style={{ flex: 1, padding: '5px 0', borderRadius: R.sm, border: `1px solid ${!showConsolidated ? C.accent : C.border}`, background: !showConsolidated ? C.accentLt : C.white, color: !showConsolidated ? C.accent : C.muted, fontSize: T.xs, fontWeight: !showConsolidated ? 700 : 400, cursor: 'pointer' }}>
            별도재무제표
          </button>
        </div>
      )}
      {!hasBoth && activeData.fnclDcdNm && (
        <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 8 }}>{activeData.fnclDcdNm}</div>
      )}

      {/* 재무상태표 */}
      <div style={{ marginBottom: 12, padding: 10, borderRadius: R.md, background: C.bgAlt, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 6 }}>재무상태표</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.xs }}>
          <tbody>
            {([['유동자산', activeData.currentAssets],['비유동자산', (activeData.totalAssets||0)-(activeData.currentAssets||0)],['자산총계', activeData.totalAssets],['유동부채', activeData.currentLiabilities],['비유동부채', (activeData.totalDebt||0)-(activeData.currentLiabilities||0)],['부채총계', activeData.totalDebt],['자본총계', activeData.equity]] as [string, number|undefined|null][]).map(([label, val]) => (
              <tr key={label} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '6px 8px', color: C.muted, fontWeight: label.includes('총계') ? 700 : 400 }}>{label}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: label.includes('총계') ? 700 : 400, ...negStyle(val ?? null) }}>{fmtEok(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 손익계산서 */}
      <div style={{ marginBottom: 12, padding: 10, borderRadius: R.md, background: C.bgAlt, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 6 }}>손익계산서</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.xs }}>
          <tbody>
            {([['매출액', revenue],['영업이익', opProfit],['당기순이익', netInc]] as [string, number][]).map(([label, val]) => (
              <tr key={label} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '6px 8px', color: C.muted }}>{label}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', ...negStyle(val) }}>{fmtEok(val)}</td>
              </tr>
            ))}
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '6px 8px', color: C.muted }}>영업이익률</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', ...negStyle(opMargin) }}>{opMargin != null ? fmtPct(opMargin) : '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
